import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { TextInput } from '@tanium/react-text-input';
import {
  TaniumIcon,
  CircleCheckIcon,
  StateCriticalIcon,
  ExternalLinkIcon,
  CircleInfoIcon,
  NoneIcon,
  OptionsIcon,
  PreviewIcon,
} from '@tanium/react-graphics';
import {
  StackedRibbonListMinimalStatic,
  StackedRibbonListWrapper,
  RibbonColumn,
  SortChangeEvent,
  SortItem,
} from '@tanium/react-stacked-ribbon-list';
import { Label } from '@tanium/react-label';
import { Box } from '@tanium/react-box';
import { Button, TooltippedButton } from '@tanium/react-button';
import { PageSection } from '@tanium/react-page-section';
import { Well } from '@tanium/react-well';
import { TextWithIcon } from '@tanium/react-text-with-icon';
import { NavSectionLeaf } from '@tanium/react-workbench';
import { Shimmer } from '@tanium/react-shimmer';
import { NotificationBase } from '@tanium/react-notifications-base';
import { Notification } from '@tanium/react-persistent-notifications';
import { Stack, StackChild } from '@tanium/react-stack';
import { Menu, MenuItem } from '@tanium/react-menu';
import { ThemeColors } from '@tanium/theme-data';
import { ConsoleAccessStatus, InstanceStatus, TaniumInstance, ComplianceFramework } from '../../types';
import { alwaysTrueOp, sortData } from '../../utilities/commonFuncs';
import { formatDate, getDateFromDateOnlyString } from '../../utilities/dateHelper';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { HostnamesCell } from '../../components/HostnamesCell';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';
import { useTenant } from '../../hooks/useTenant';
import { DeactivateCEModal } from './DeactivateCEModal';
import { ReactivateCEModal } from './ReactivateCEModal';
import { RemoveFromListModal } from './RemoveFromListModal';
import { AccountDetailsSection } from './AccountDetailsSection';
import { AsyncStatus } from '../../hooks/useAsync';
import { IdentityProvidersSection } from './IdentityProvidersSection';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { getRegionDisplayName } from '../../utilities/constants';
import { instanceStatusDisplay } from '../../utilities/instanceStatusDisplay';
import {
  canNavigateToCmp,
  getTaniumConsoleLink,
  isLinkedCloudInstance,
  isOnPremLicenseDownloadable,
  parseHostnames,
} from '../../utilities/instanceHelper';
import { useHasPermissions } from '../../hooks/useUserPermissions';
import { Permission } from '../../models/Permissions';
import { CloudRoutes, getCloudInstanceRoutes, LICENSES_ROUTE_URL } from '../../Routes';
import { useComplianceFramework } from '../../hooks/useComplianceFramework';
import { MediumBoldText, SmallSubtleText } from 'TextStyles';
import { useTenantEndpointCount } from '../../hooks/useTenantEndpointCount';
import { useConsoleAccessStatuses } from '../../hooks/useConsoleAccessStatuses';
import { MetricsSection } from './MetricsSection';
import { BillingSection } from './Billing/BillingSection';

function needsConsoleUser({
  instance,
  consoleAccess,
}: {
  instance: TaniumInstance;
  consoleAccess: ConsoleAccessStatus;
}): boolean {
  return (
    isLinkedCloudInstance(instance) && canNavigateToCmp(instance) && consoleAccess === ConsoleAccessStatus.NotConfigured
  );
}

function linkedInstanceEndpointCountLabel(instance: TaniumInstance): string {
  if (
    instance.endpointCount === undefined ||
    instance.status === InstanceStatus.Creating ||
    instance.status === InstanceStatus.Deleted
  ) {
    return '--';
  }
  return instance.endpointCount.toString();
}

export const ManageAccountSection: React.VFC = () => {
  const { t } = useTranslation();
  const cloudInstanceRoutes = getCloudInstanceRoutes(t);
  const cloudRoutes = Object.values(cloudInstanceRoutes);

  const [deactivateCEModalOpen, setDeactivateCEModalOpen] = useState({ open: false, instanceId: '', instanceName: '' });
  const [reactivateCEModalOpen, setReactivateCEModalOpen] = useState({ open: false, instanceId: '', instanceName: '' });
  const [removeFromListModalOpen, setRemoveFromListModalOpen] = useState({
    open: false,
    instanceId: '',
    instanceName: '',
  });
  const [taniumInstanceSort, setTaniumInstanceSort] = useState<SortItem[]>([]);
  const [taniumInstanceFilter, setTaniumInstanceFilter] = useState('');
  const { loadLinkedTaniumInstances, linkedTaniumInstances } = useTaniumInstances();
  // Linked instances only — pending "available" rows must not count toward summaries or the MSP limit.
  const linkedOnlyInstances = linkedTaniumInstances.filter((i) => !i.isAvailable);
  const sortedTaniumInstances = useMemo(
    () => sortData(taniumInstanceSort, linkedTaniumInstances),
    [linkedTaniumInstances, taniumInstanceSort]
  );
  const filteredTaniumInstances = useMemo(() => {
    const query = taniumInstanceFilter.trim().toLowerCase();
    if (!query) return sortedTaniumInstances;
    return sortedTaniumInstances.filter((instance) => {
      const fields = [
        instance.url,
        instance.hostnames,
        getRegionDisplayName(instance.region),
        instance.licenseId,
        instance.friendlyName,
        instance.customerName,
        instance.status,
      ];
      return fields.some((field) => field != null && String(field).toLowerCase().includes(query));
    });
  }, [sortedTaniumInstances, taniumInstanceFilter]);
  const onTaniumInstanceSortChange = useCallback(({ sort }: SortChangeEvent) => {
    setTaniumInstanceSort(sort);
  }, []);
  const { idpConfigs, loadIdps } = useIdentityProviderConfigs();
  const { tenant } = useTenant();
  const { endpointCount, loadTenantEndpointCount } = useTenantEndpointCount(Boolean(tenant?.isMsp));
  const activeCloudInstanceIds = linkedOnlyInstances
    .filter((i) => i.isCloudInstance && i.status === InstanceStatus.Active)
    .map((i) => i.id)
    .sort();
  const { getConsoleAccess } = useConsoleAccessStatuses({
    enabled: activeCloudInstanceIds.length > 0,
    instanceIdsKey: activeCloudInstanceIds.join(','),
  });
  const { showNotification } = useToastNotificationsControl();

  useEffect(() => {
    if (loadTenantEndpointCount.status === AsyncStatus.Failure) {
      showNotification({
        type: 'error',
        contents: t('Failed to load endpoint count'),
      });
    }
  }, [loadTenantEndpointCount.status, showNotification, t]);

  const canCreateOrUpdateIdp = useHasPermissions([Permission.CreateIdp, Permission.UpdateIdp]);
  const addIdpTooltip = canCreateOrUpdateIdp
    ? t('Add an Identity Provider')
    : t('You have insufficient permissions to add an IDP.');
  const canAddIdp = canCreateOrUpdateIdp;

  const canDeactivateInstance = useHasPermissions([Permission.DeactivateInstance]);
  const canReactivateInstance = useHasPermissions([Permission.ReactivateInstance]);
  const canRemoveInstance = useHasPermissions([Permission.RemoveDeletedInstance]);
  const canCreateNewCE = useHasPermissions([Permission.CreateNewCE]);
  const complianceFramework = useComplianceFramework();

  const DEACTIVATE_ACTION = 'deactivate';
  const REACTIVATE_ACTION = 'reactivate';
  const REMOVE_FROM_LIST_ACTION = 'removeFromList';

  type ManageInstanceOption = {
    value: NavSectionLeaf | string;
    label: string;
    isDisabled?: boolean;
  };

  const buildManageInstanceMspOptions = (itemData: TaniumInstance): ManageInstanceOption[] => {
    const options: ManageInstanceOption[] = cloudRoutes.map((route) => ({
      value: route,
      label: route.name,
    }));

    if (tenant?.isMsp && itemData.status !== InstanceStatus.Deactivated) {
      options.push({
        value: DEACTIVATE_ACTION,
        label: t('Deactivate'),
      });
    }

    if (tenant?.isMsp && itemData.status === InstanceStatus.Deactivated) {
      options.push({
        value: REACTIVATE_ACTION,
        label: t('Reactivate'),
      });
    }

    if (tenant?.isMsp && itemData.status === InstanceStatus.Deleted) {
      options.push({
        value: REMOVE_FROM_LIST_ACTION,
        label: t('Remove from List'),
      });
    }

    return options;
  };

  // Toolbar Actions menu only; instance status rules differ from the legacy Manage Instance select.
  const applyManageInstanceToolbarMenuOptionAvailability = (
    options: ManageInstanceOption[],
    itemData: TaniumInstance
  ): ManageInstanceOption[] => {
    const status = itemData.status;

    const allRoutesDisabledStatuses: InstanceStatus[] = [InstanceStatus.Creating, InstanceStatus.Deleting];

    if (status !== undefined && allRoutesDisabledStatuses.includes(status)) {
      return options.map((opt) => ({ ...opt, isDisabled: true }));
    }

    const cmpEnabledNonActiveStatuses: InstanceStatus[] = [InstanceStatus.Deactivating, InstanceStatus.Reactivating];

    if (status !== undefined && cmpEnabledNonActiveStatuses.includes(status)) {
      return options.map((opt) => ({
        ...opt,
        isDisabled: typeof opt.value === 'string',
      }));
    }

    if (status === InstanceStatus.Deleted) {
      return options.map((opt) => ({
        ...opt,
        isDisabled: opt.value === REMOVE_FROM_LIST_ACTION ? !canRemoveInstance : true,
      }));
    }

    if (status === InstanceStatus.Deactivated) {
      return options.map((opt) => {
        if (opt.value === REACTIVATE_ACTION) {
          return { ...opt, isDisabled: !canReactivateInstance };
        }
        return { ...opt, isDisabled: false };
      });
    }

    if (status === InstanceStatus.Active) {
      return options.map((opt) => {
        if (opt.value === DEACTIVATE_ACTION) {
          return { ...opt, isDisabled: !canDeactivateInstance };
        }
        return { ...opt, isDisabled: false };
      });
    }

    const mapUnknownLikeToolbarAvailability = (): ManageInstanceOption[] =>
      options.map((opt) => ({
        ...opt,
        isDisabled: opt.value === DEACTIVATE_ACTION,
      }));

    const unknownLikeStatuses = new Set<InstanceStatus | undefined>([InstanceStatus.Unknown, undefined]);
    /* istanbul ignore next — Set.has branch pairs are fully exercised; Istanbul still reports partial branches */
    if (unknownLikeStatuses.has(status)) {
      return mapUnknownLikeToolbarAvailability();
    }

    /* istanbul ignore next — all InstanceStatus values handled above */
    return options.map((opt) => ({ ...opt, isDisabled: true }));
  };

  const getManageInstanceToolbarMenuOptions = (itemData: TaniumInstance): ManageInstanceOption[] => {
    return applyManageInstanceToolbarMenuOptionAvailability(buildManageInstanceMspOptions(itemData), itemData);
  };

  const accountSummarySection = () => {
    const hardcodedSolutions = [
      t('Tanium Core Plus'),
      t('Endpoint Management Plus'),
      t('Exposure Management'),
      t('Security Operations'),
      t('Operational Technology'),
      t('AI Usage'),
    ];
    const sectionPadding: React.CSSProperties = { padding: '0 12px' };

    return (
      <Well bg={ThemeColors.Light0}>
        <Stack itemSpacing={2} itemDividers>
          <Stack direction="column" itemSpacing={1} style={sectionPadding}>
            <SmallSubtleText>{t('Account Name')}</SmallSubtleText>
            <MediumBoldText>{tenant?.accountName}</MediumBoldText>
          </Stack>
          <Stack direction="column" itemSpacing={1} style={sectionPadding}>
            <SmallSubtleText>{t('Account Type')}</SmallSubtleText>
            <MediumBoldText>{t('Managed Service Provider')}</MediumBoldText>
          </Stack>
          <StackChild grow={1}>
            <Stack direction="column" itemSpacing={1} style={sectionPadding}>
              <SmallSubtleText>{t('Solutions')}</SmallSubtleText>
              <Stack itemSpacing={1} style={{ flexWrap: 'wrap' }}>
                {hardcodedSolutions.map((solution) => {
                  return <Label key={solution}>{solution}</Label>;
                })}
              </Stack>
            </Stack>
          </StackChild>
          <Stack direction="column" itemSpacing={1} style={sectionPadding}>
            <SmallSubtleText>{t('Tanium Instances')}</SmallSubtleText>
            <MediumBoldText data-testid="account-summary-tanium-instances">
              {loadLinkedTaniumInstances.status === AsyncStatus.Pending ? '--' : linkedOnlyInstances.length}
            </MediumBoldText>
          </Stack>
          <Stack direction="column" itemSpacing={1} style={sectionPadding}>
            <SmallSubtleText>{t('Endpoints')}</SmallSubtleText>
            <MediumBoldText>
              {loadTenantEndpointCount.status === AsyncStatus.Pending ||
              loadTenantEndpointCount.status === AsyncStatus.Failure
                ? '--'
                : endpointCount ?? 0}
            </MediumBoldText>
          </Stack>
        </Stack>
      </Well>
    );
  };

  const taniumInstancesSection = () => {
    const instances = filteredTaniumInstances;

    const openInNewWindow = (itemData: TaniumInstance) => {
      const ceDomain = itemData.url;
      window.open(getTaniumConsoleLink(ceDomain), '_blank', 'noopener,noreferrer');
    };

    const goToInstance = (itemData: TaniumInstance, route: NavSectionLeaf) => {
      window.location.href = `#${route.url}?id=${itemData.id}`;
    };

    const handleManageInstanceSelection = (itemData: TaniumInstance, selected: NavSectionLeaf | string) => {
      if (selected === DEACTIVATE_ACTION) {
        setDeactivateCEModalOpen({
          open: true,
          instanceId: itemData.id,
          instanceName: itemData.url,
        });
      } else if (selected === REACTIVATE_ACTION) {
        setReactivateCEModalOpen({
          open: true,
          instanceId: itemData.id,
          instanceName: itemData.url,
        });
      } else if (selected === REMOVE_FROM_LIST_ACTION) {
        setRemoveFromListModalOpen({
          open: true,
          instanceId: itemData.id,
          instanceName: itemData.url,
        });
      } else {
        goToInstance(itemData, selected as NavSectionLeaf);
      }
    };

    const manageInstanceMenuDisabledTooltip = (
      opt: ManageInstanceOption,
      itemData: TaniumInstance
    ): string | undefined => {
      if (!opt.isDisabled) {
        return undefined;
      }
      const status = itemData.status;

      if (opt.value === DEACTIVATE_ACTION) {
        if (!canDeactivateInstance) {
          return t('You have insufficient permissions to deactivate this instance.');
        }
        if (status === InstanceStatus.Unknown || status === undefined) {
          return t('Deactivation is not available when the instance status is unknown.');
        }
        return t('Only active instances can be deactivated.');
      }

      if (opt.value === REACTIVATE_ACTION) {
        return t('You have insufficient permissions to reactivate this instance.');
      }

      if (opt.value === REMOVE_FROM_LIST_ACTION) {
        return t('You have insufficient permissions to remove this instance.');
      }

      const routeUnavailableMessage = t('This action is not available for the current instance state.');
      const routeUnavailableStatuses = new Set<InstanceStatus>([
        InstanceStatus.Creating,
        InstanceStatus.Deleting,
        InstanceStatus.Deleted,
      ]);
      /* istanbul ignore next — Set.has branch pairs are fully exercised; Istanbul still reports partial branches */
      if (routeUnavailableStatuses.has(status as InstanceStatus)) {
        return routeUnavailableMessage;
      }

      /* istanbul ignore next — route options use status-specific messages above */
      return routeUnavailableMessage;
    };

    const healthColumn: RibbonColumn<TaniumInstance> = {
      label: t('Health'),
      helpText: t('Current Health'),
      dataKey: 'isUp',
      width: 110,
      isSortDisabled: true,
      alignment: 'center',
      renderer: ({ itemData }) => {
        if (!itemData.isCloudInstance) {
          return (
            <TooltippedIconButton
              label={t('Data not available for on-premise instances')}
              tooltipContent={t('Data not available for on-premise instances')}
              icon={NoneIcon}
              height={16}
              width={16}
              onClick={() => {}}
              style={{ color: 'gray' }}
            />
          );
        }

        if (itemData.isUp === true) {
          return (
            <TooltippedIconButton
              label={t('Operational')}
              tooltipContent={t('Operational')}
              icon={CircleCheckIcon}
              height={16}
              width={16}
              onClick={() => {}}
              style={{ color: 'green' }}
            />
          );
        }
        if (itemData.isUp === false) {
          if (itemData.isInMaintenanceWindow === true) {
            return (
              <TooltippedIconButton
                label={t('Active maintenance is ongoing')}
                tooltipContent={t('Active maintenance is ongoing')}
                icon={CircleCheckIcon}
                height={16}
                width={16}
                onClick={() => {}}
                style={{ color: 'gray' }}
              />
            );
          }
          if (itemData.isInMaintenanceWindow === false) {
            return (
              <TooltippedIconButton
                label={t('Tanium is aware of a system disruption')}
                tooltipContent={t('Tanium is aware of a system disruption')}
                icon={StateCriticalIcon}
                height={16}
                width={16}
                onClick={() => {}}
                style={{ color: 'red' }}
              />
            );
          }
        }
        return (
          <TooltippedIconButton
            label={t('Data not currently available')}
            tooltipContent={t('Data not currently available')}
            icon={NoneIcon}
            height={16}
            width={16}
            onClick={() => {}}
            style={{ color: 'gray' }}
          />
        );
      },
    };

    const friendlyNameColumn: RibbonColumn<TaniumInstance> = {
      label: t('Tanium Instance'),
      dataKey: 'friendlyName',
      grow: 1,
      isSortDisabled: false,
      renderer: ({ itemData }) => {
        const overviewRoute = cloudInstanceRoutes[CloudRoutes.Overview];
        if (itemData.isAvailable) {
          return <span style={{ fontWeight: 'bold' }}>{itemData.customerName}</span>;
        }
        if (!itemData.isCloudInstance || itemData.status !== InstanceStatus.Active) {
          return <span style={{ fontWeight: 'bold' }}>{itemData.friendlyName}</span>;
        }
        return (
          <Button variant="link" onClick={() => goToInstance(itemData, overviewRoute)} style={{ fontWeight: 'bold' }}>
            {itemData.friendlyName}
          </Button>
        );
      },
    };

    const columns: RibbonColumn<TaniumInstance>[] = [
      // only show friendlyNameColumn for MSP customers, other customers don't have good friendly name values and can't edit it.
      ...(tenant?.isMsp ? [friendlyNameColumn] : []),
      {
        label: t('Hostname'),
        dataKey: 'url',
        grow: 2,
        noWrap: false,
        isSortDisabled: false,
        // A pending row has no linked instance to take a URL from, so it shows the hostname the
        // offered license carries. A promoted on-prem instance keeps its whole hostname list in url,
        // so both sources can be a list and both truncate for display.
        renderer: ({ itemData }: { itemData: TaniumInstance }) => (
          <HostnamesCell hostnames={itemData.isAvailable ? itemData.hostnames : itemData.url} />
        ),
      } as RibbonColumn<TaniumInstance>,
      {
        label: t('Region'),
        dataKey: 'region',
        renderer: ({ itemData }: { itemData: TaniumInstance }) => {
          // On-prem deployments have no cloud region, so getRegionDisplayName falls through to
          // "Unknown", which reads as missing data rather than as the deployment type. Pending
          // offers are on-prem too, and carry no isCloudInstance, so they land here as well.
          if (!itemData.isCloudInstance) {
            return <Label>{t('On-Premise')}</Label>;
          }
          return <Label>{t(getRegionDisplayName(itemData.region))}</Label>;
        },
        isSortDisabled: false,
      } as RibbonColumn<TaniumInstance>,
      {
        label: t('Status'),
        dataKey: 'status',
        grow: 1,
        isSortDisabled: false,
        renderer: ({ itemData }: { itemData: TaniumInstance }) => {
          if (itemData.isAvailable) {
            return (
              <Stack direction="row" itemSpacing={1} alignItems="center" inline>
                <CircleInfoIcon height={16} width={16} color="#006FCC" aria-hidden />
                <span>{t('Available')}</span>
              </Stack>
            );
          }
          const { StatusGlyph, iconColor, text } = instanceStatusDisplay(itemData.status);
          return (
            <Stack direction="row" itemSpacing={1} alignItems="center" inline>
              <StatusGlyph height={16} width={16} color={iconColor} aria-hidden />
              <span>{text}</span>
            </Stack>
          );
        },
      } as RibbonColumn<TaniumInstance>,
      {
        label: t('License ID'),
        dataKey: 'licenseId',
        grow: 1,
        isSortDisabled: false,
        // Until AMB onboards the deployment, the stored license ID is only a best guess at which
        // license the instance runs (an Available row is an unclaimed offer). Do not display the guess.
        renderer: ({ itemData }: { itemData: TaniumInstance }) =>
          itemData.isAvailable || itemData.status === InstanceStatus.Licensed ? '--' : itemData.licenseId,
      } as RibbonColumn<TaniumInstance>,
      ...(complianceFramework !== ComplianceFramework.USGov
        ? [
            {
              label: t('Managed Endpoints'),
              dataKey: 'endpointCount',
              grow: 1,
              isSortDisabled: false,
              alignment: 'center' as const,
              renderer: ({ itemData }: { itemData: TaniumInstance }) => linkedInstanceEndpointCountLabel(itemData),
            },
          ]
        : []),
      ...(complianceFramework !== ComplianceFramework.USGov ? [healthColumn] : []),
      {
        label: t('Linked Date'),
        dataKey: 'createdAt',
        grow: 1,
        isSortDisabled: false,
        renderer: ({ itemData }) => {
          // Pending rows are not linked yet and carry a zero createdAt — show nothing rather than "0001-01-01".
          return itemData.isAvailable ? '--' : formatDate(new Date(itemData.createdAt));
        },
      },
      ...(!tenant?.isMsp
        ? [
            {
              label: t('License Expiration Date'),
              dataKey: 'licenseExpirationDate',
              grow: 1,
              isSortDisabled: false,
              renderer: ({ itemData }: { itemData: TaniumInstance }) => {
                // The expiry belongs to the guessed license, so it is hidden on the same terms as
                // the License ID until AMB onboarding proves which license the deployment runs.
                if (itemData.isAvailable || itemData.status === InstanceStatus.Licensed) {
                  return '--';
                }
                return itemData.licenseExpirationDate
                  ? formatDate(getDateFromDateOnlyString(itemData.licenseExpirationDate))
                  : null;
              },
            } as RibbonColumn<TaniumInstance>,
          ]
        : []),
      {
        label: t('Actions'),
        isSortDisabled: true,
        width: 88,
        dataKey: 'actions',
        alignment: 'right' as const,
        renderer: ({ itemData }) => {
          if (isOnPremLicenseDownloadable(itemData)) {
            return (
              <Stack direction="row" itemSpacing={1} alignItems="center" inline justifyContent="flex-end">
                <TooltippedIconButton
                  icon={PreviewIcon}
                  label={t('View Licenses')}
                  tooltipContent={t('View Licenses')}
                  width={16}
                  height={16}
                  onClick={() => {
                    // Filter the Licenses page to this deployment by its first hostname -- the value
                    // the Hostname column shows here and the Licenses page matches on. A row with no
                    // hostname lands unfiltered rather than seeding a filter that matches nothing.
                    const [firstHostname] = parseHostnames(itemData.isAvailable ? itemData.hostnames : itemData.url);
                    const search = firstHostname ? `?filter=${encodeURIComponent(firstHostname)}` : '';
                    window.location.href = `#${LICENSES_ROUTE_URL}${search}`;
                  }}
                />
              </Stack>
            );
          }
          if (!itemData.isCloudInstance) {
            return null;
          }
          const menuOptions = getManageInstanceToolbarMenuOptions(itemData);
          const needsUser = needsConsoleUser({
            instance: itemData,
            consoleAccess: getConsoleAccess({ instanceId: itemData.id }),
          });
          let consoleTooltip = t('Go to Console');
          if (itemData.status !== InstanceStatus.Active) {
            consoleTooltip = t('This instance is not Active');
          } else if (needsUser) {
            consoleTooltip = t(
              'Add an identity provider or Local User for this instance before going to its Tanium Console.'
            );
          }
          return (
            <Stack direction="row" itemSpacing={1} alignItems="center" inline justifyContent="flex-end">
              <TooltippedIconButton
                icon={ExternalLinkIcon}
                label={t('Go to Console')}
                disabled={itemData.status !== InstanceStatus.Active || needsUser}
                tooltipContent={consoleTooltip}
                width={16}
                height={16}
                onClick={() => openInNewWindow(itemData)}
              />
              <Menu
                placement="bottom-end"
                size="sm"
                content={menuOptions.map((opt, index) => (
                  <MenuItem
                    key={`${opt.label}-${index}`}
                    disabled={opt.isDisabled}
                    disabledTooltip={manageInstanceMenuDisabledTooltip(opt, itemData)}
                    onAction={() => handleManageInstanceSelection(itemData, opt.value)}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              >
                {({ ref, show }) => (
                  <TooltippedIconButton
                    ref={ref}
                    icon={OptionsIcon}
                    label={t('Manage Instance')}
                    tooltipContent={t('Manage Instance')}
                    width={16}
                    height={16}
                    onClick={show}
                  />
                )}
              </Menu>
            </Stack>
          );
        },
      } as RibbonColumn<TaniumInstance>,
    ];

    return (
      <PageSection
        title={<TextWithIcon leadingIcon={TaniumIcon}>{t('Tanium Instances')}</TextWithIcon>}
        additionalTitleContent={
          <Stack width="100%" direction="row" justifyContent="right" alignItems="center" itemSpacing={2}>
            <TextInput
              aria-label={t('Filter instances')}
              placeholder={t('Filter instances...')}
              value={taniumInstanceFilter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTaniumInstanceFilter(e.target.value)}
              style={{ width: '200px' }}
            />
            {tenant?.isMsp && canCreateNewCE && (
              <TooltippedButton
                size="sm"
                onClick={() => {
                  window.location.href = '#/create-instance';
                }}
                disabled={
                  loadLinkedTaniumInstances.status === AsyncStatus.Pending ||
                  (tenant.mspInstanceLimit != null &&
                    linkedOnlyInstances.filter((i) => i.status !== InstanceStatus.Deleted).length >=
                      tenant.mspInstanceLimit)
                }
                tooltipContent={
                  tenant.mspInstanceLimit != null &&
                  linkedOnlyInstances.filter((i) => i.status !== InstanceStatus.Deleted).length >=
                    tenant.mspInstanceLimit
                    ? t(
                        'You have reached your MSP instance limit of {{limit}}. Please contact Tanium to increase your limit.',
                        { limit: tenant.mspInstanceLimit }
                      )
                    : t('Create a new Tanium Instance')
                }
              >
                {t('Create Tanium Instance')}
              </TooltippedButton>
            )}
          </Stack>
        }
      >
        <AsyncSuspense
          state={loadLinkedTaniumInstances}
          loading={
            <Box mt={2} height={1} width="100%">
              <Shimmer />
            </Box>
          }
          failure={t('Failed to load Tanium Instances')}
        >
          <div>
            {instances.length ? (
              <StackedRibbonListWrapper>
                <StackedRibbonListMinimalStatic
                  columns={columns}
                  dataGetter={(i: number) => instances[i]}
                  isItemLoaded={alwaysTrueOp}
                  itemCount={instances.length}
                  itemIdGetter={(i: number) => instances[i].id}
                  sort={taniumInstanceSort}
                  onSortChange={onTaniumInstanceSortChange}
                ></StackedRibbonListMinimalStatic>
              </StackedRibbonListWrapper>
            ) : (
              <Notification role="note" type="info">
                {t('No Tanium Instances are linked to this account.')}
              </Notification>
            )}
          </div>
        </AsyncSuspense>
      </PageSection>
    );
  };

  return (
    <div>
      {loadIdps.status === AsyncStatus.Success && idpConfigs.length === 0 && (
        <Box mt={1}>
          <NotificationBase role="note" type="info" style={{ alignItems: 'center' }}>
            <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>
                {t(
                  'At least one Identity Provider (IDP) must be configured to grant users access to your Tanium Account.'
                )}
              </span>
              <TooltippedButton
                size="sm"
                disabled={!canAddIdp}
                tooltipContent={addIdpTooltip}
                onClick={() => {
                  // Replace rather than push: the IdP section below clears the param at once, so a pushed entry
                  // would make the next Back press do nothing.
                  window.location.replace('#/?openAddIdp=true');
                }}
              >
                {t('Add IDP')}
              </TooltippedButton>
            </span>
          </NotificationBase>
        </Box>
      )}
      {tenant?.isMsp && (
        <Well mt={1} p={1}>
          {accountSummarySection()}
        </Well>
      )}
      {tenant?.isMsp && complianceFramework !== ComplianceFramework.USGov && (
        // TODO(AMP-1229): show metrics to all customer types
        <Well mt={1} p={1}>
          <MetricsSection />
        </Well>
      )}
      <Well mt={1} p={1}>
        {taniumInstancesSection()}
      </Well>
      {tenant?.selfServeBillingEnabled && !tenant.isMsp && (
        <Well mt={1} p={1}>
          <BillingSection />
        </Well>
      )}
      <Well mt={1} p={1}>
        <IdentityProvidersSection showInstanceFriendlyNames={tenant?.isMsp} />
      </Well>
      <Well mt={1} p={1}>
        <AccountDetailsSection />
      </Well>
      <DeactivateCEModal
        instanceId={deactivateCEModalOpen.instanceId}
        instanceName={deactivateCEModalOpen.instanceName}
        isOpen={deactivateCEModalOpen.open}
        onRequestClose={() => setDeactivateCEModalOpen({ open: false, instanceId: '', instanceName: '' })}
      />
      <ReactivateCEModal
        instanceId={reactivateCEModalOpen.instanceId}
        instanceName={reactivateCEModalOpen.instanceName}
        isOpen={reactivateCEModalOpen.open}
        onRequestClose={() => setReactivateCEModalOpen({ open: false, instanceId: '', instanceName: '' })}
      />
      <RemoveFromListModal
        instanceId={removeFromListModalOpen.instanceId}
        instanceName={removeFromListModalOpen.instanceName}
        isOpen={removeFromListModalOpen.open}
        onRequestClose={() => setRemoveFromListModalOpen({ open: false, instanceId: '', instanceName: '' })}
      />
    </div>
  );
};
